import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { taskNavRef } from '../utils/taskNav';

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

export default function MyAttemptsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [filterTask, setFilterTask] = useState(searchParams.get('task') || '');
  const [filterListItem, setFilterListItem] = useState(searchParams.get('task_list_item') || '');

  const queryParams = useMemo(() => {
    const params = {};
    if (filterTask.trim()) params.task = filterTask.trim();
    if (filterListItem.trim()) params.task_list_item = filterListItem.trim();
    return params;
  }, [filterTask, filterListItem]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTask, filterListItem]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/tasks/attempts/', { params: queryParams });
      const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setAttempts(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить попытки.');
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }

  function applyFilters() {
    const next = new URLSearchParams();
    if (filterTask.trim()) next.set('task', filterTask.trim());
    if (filterListItem.trim()) next.set('task_list_item', filterListItem.trim());
    setSearchParams(next, { replace: true });
    load();
  }

  const selected = attempts.find((a) => a.id === selectedId) || null;

  return (
    <div className="my-attempts-page">
      <div className="page-header">
        <h1>Мои попытки</h1>
        <p className="page-subtitle">
          Здесь хранится история сохранённых попыток: код, самооценка, отчёт и комментарии преподавателя (если есть).
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="attempts-filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <input
          className="form-input"
          style={{ maxWidth: '18rem' }}
          value={filterTask}
          onChange={(e) => setFilterTask(e.target.value)}
          placeholder="Фильтр: задача (slug или id)"
        />
        <input
          className="form-input"
          style={{ maxWidth: '14rem' }}
          value={filterListItem}
          onChange={(e) => setFilterListItem(e.target.value)}
          placeholder="Фильтр: task_list_item id"
        />
        <button type="button" className="btn btn-outline" onClick={applyFilters} disabled={loading}>
          Применить
        </button>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => {
            setFilterTask('');
            setFilterListItem('');
            setSearchParams(new URLSearchParams(), { replace: true });
          }}
          disabled={loading}
        >
          Сбросить
        </button>
      </div>

      {loading ? (
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Загрузка...</p>
        </div>
      ) : attempts.length === 0 ? (
        <p className="text-secondary">Пока нет сохранённых попыток.</p>
      ) : (
        <div className="attempts-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 2fr', gap: '0.75rem' }}>
          <aside style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <div style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
              <strong>Список</strong>
            </div>
            <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
              {attempts.map((a) => {
                const label = SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '—';
                const dt = a.created_at ? new Date(a.created_at).toLocaleString('ru-RU') : `#${a.id}`;
                const active = a.id === selectedId;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelectedId(a.id)}
                    className={`attempt-row ${active ? 'active' : ''}`}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.5rem',
                      border: 'none',
                      borderBottom: '1px solid var(--border)',
                      background: active ? 'rgba(255,255,255,0.06)' : 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span className="text-secondary" style={{ fontSize: '0.85rem' }}>{dt}</span>
                      <span style={{ fontFamily: 'var(--font-code)' }}>{label}</span>
                    </div>
                    <div style={{ marginTop: '0.15rem' }}>
                      <span>{a.task_title || a.task_slug || `Задача #${a.task}`}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <main style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '0.75rem', minWidth: 0 }}>
            {!selected ? (
              <p className="text-secondary">Выберите попытку слева.</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <h2 style={{ margin: 0 }}>{selected.task_title || selected.task_slug || 'Попытка'}</h2>
                  <span className="badge">
                    {SELF_REPORT_LABELS[selected.self_report_status] || selected.self_report_status || '—'}
                  </span>
                  {selected.task_slug && (
                    <Link to={`/work/${taskNavRef({ slug: selected.task_slug, id: selected.task })}`} className="btn btn-outline btn-sm">
                      Открыть задачу
                    </Link>
                  )}
                </div>

                {selected.self_comment ? (
                  <div style={{ marginTop: '0.75rem' }}>
                    <strong>Отчёт/комментарий</strong>
                    <div className="text-secondary" style={{ whiteSpace: 'pre-wrap', marginTop: '0.35rem' }}>
                      {selected.self_comment}
                    </div>
                  </div>
                ) : null}

                {selected.teacher_comment ? (
                  <div style={{ marginTop: '0.75rem' }}>
                    <strong>Комментарий преподавателя</strong>
                    <div className="text-secondary" style={{ whiteSpace: 'pre-wrap', marginTop: '0.35rem' }}>
                      {selected.teacher_comment}
                    </div>
                  </div>
                ) : null}

                <div style={{ marginTop: '0.75rem' }}>
                  <strong>Код</strong>
                  <pre className="code-snapshot" style={{ marginTop: '0.35rem', maxHeight: '50vh', overflow: 'auto' }}>
                    {selected.code_snapshot || '(пусто)'}
                  </pre>
                </div>
              </>
            )}
          </main>
        </div>
      )}
    </div>
  );
}

